import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import { CharacterCount, UndoRedo } from '@tiptap/extensions';
import {
	Editor,
	EditorContent,
	EditorContext,
	type Extensions,
	useCurrentEditor,
	useEditor,
	type UseEditorOptions,
} from '@tiptap/react';
import {
	type PropsWithChildren,
	type RefObject,
	useEffect,
	useMemo,
} from 'react';

import {
	InlineDocument,
	LineBreakDocument,
	LineBreakHardBreak,
	Enter,
	editorContentToTextWithLineBreaks,
	lineBreaksToHardBreaks,
	handlePasteConvertingNewLinesToHardBreaks,
} from '@/components/EditableText/tiptap-extensions.ts';
import { cn } from '@/utils/cn.ts';

export const TiptapEditor = () => {
	const { editor } = useCurrentEditor();

	return <EditorContent editor={editor} />;
};

type TiptapContextProps = {
	initialContent: string;
	controlledContent?: string;
	placeholder?: string;
	multiline: boolean;
	charLimit?: number;
	editorClassName?: string;
	placeholderClassName?: string;
	onMount?: (text: string) => void;
	onFocus?: (text: string) => void;
	onEdit?: (newText: string) => void;
	onBlur?: (text: string) => void;
	onEnter?: (text: string) => boolean;
	editorRef?: RefObject<Editor | null>;
};

export const TiptapContext = ({
	initialContent,
	controlledContent,
	placeholder,
	multiline,
	charLimit,
	children,
	editorClassName = '',
	placeholderClassName = '',
	onMount,
	onFocus,
	onEdit,
	onBlur,
	onEnter,
	editorRef,
}: PropsWithChildren<TiptapContextProps>) => {
	const extensions: Extensions = [
		Text,
		UndoRedo,
		...(multiline ? [LineBreakDocument, LineBreakHardBreak] : [InlineDocument]),
		...(charLimit ? [CharacterCount.configure({ limit: charLimit })] : []),
		// other extensions go here
	];

	if (onEnter) {
		extensions.push(Enter.configure({ onEnter }));
	}

	if (placeholder) {
		extensions.push(
			Placeholder.configure({
				placeholder,
				emptyEditorClass: cn(
					'before:pointer-events-none before:float-left before:h-0',
					'before:content-[attr(data-placeholder)]',
					placeholderClassName,
				),
			}),
		);
	}

	const options: UseEditorOptions = {
		extensions,
		content: lineBreaksToHardBreaks(initialContent || ''),
		editorProps: {
			attributes: { class: editorClassName },
		},
		parseOptions: { preserveWhitespace: 'full' },
	};
	if (multiline && options.editorProps) {
		options.editorProps.handlePaste = handlePasteConvertingNewLinesToHardBreaks;
	}
	if (onMount) {
		options.onCreate = ({ editor }) =>
			onMount(editorContentToTextWithLineBreaks(editor));
	}
	if (onFocus) {
		options.onFocus = ({ editor }) =>
			onFocus(editorContentToTextWithLineBreaks(editor));
	}
	if (onEdit) {
		options.onUpdate = ({ editor }) =>
			onEdit(editorContentToTextWithLineBreaks(editor));
	}
	if (onBlur) {
		options.onBlur = ({ editor }) =>
			onBlur(editorContentToTextWithLineBreaks(editor));
	}

	const editor = useEditor(options);
	const providerValue = useMemo(() => ({ editor }), [editor]);

	useEffect(() => {
		if (!editor || !controlledContent) return;

		// Only update content if it's different from what's currently in the editor
		// and if the editor is not focused (to avoid overwriting user input)
		const currentContent = editorContentToTextWithLineBreaks(editor);
		if (controlledContent !== currentContent && !editor.isFocused) {
			const htmlContent = lineBreaksToHardBreaks(controlledContent);

			editor.commands.setContent(htmlContent, {
				parseOptions: { preserveWhitespace: 'full' },
			});
		}
	}, [controlledContent, editor]);

	if (editorRef) {
		editorRef.current = editor;
	}

	return (
		<EditorContext.Provider value={providerValue}>
			{children}
		</EditorContext.Provider>
	);
};
